<template>
  <div>
    <a class="usa-skipnav" href="#main-content">Skip to main content</a>
    <USWDSBanner></USWDSBanner>
    <HeaderUSGS></HeaderUSGS>
    <USWDSAlert
      v-if="bannerMessage"
      title="Attention"
      :text="bannerMessage"
      :warning="true"
    ></USWDSAlert>
    <div class="grid-container">
      <div class="grid-row" style="margin-bottom: 25px; margin-left: 10px">
        <h2>Configuration Depot</h2>
        <div class="usa-accordion" style="border: 2px solid grey">
          <h3 class="usa-accordion__heading">
            <button
              class="usa-accordion__button"
              aria-expanded="true"
              aria-controls="b-a1"
            >
              About
            </button>
          </h3>
          <div id="b-a1" class="usa-accordion__content usa-prose">
            <p>
              The Configuration Depot is used to view and download information
              related to the transmission and decoding of telemetry data from
              gages in the USGS water observing networks.
              <br />
              <br />
              For questions and feedback contact the
              <a href="mailto:gs-w_osd_tapas@usgs.gov"> USGS telemetry team</a>
            </p>
          </div>
        </div>
      </div>
      <div id="main-content" class="grid-row">
        <div class="tablet:grid-col-4">
          <USWDSTextInput
            label="Site Number"
            @enterPressed="decideBetweenDecapOrSiteInformation"
            v-model.trim="siteNumber"
            aria-label="Site Number"
          >
          </USWDSTextInput>
          <USWDSTextInput
            label="Site Name"
            @enterPressed="decideBetweenDecapOrSiteInformation"
            v-model.trim="siteName"
            aria-label="Site Name"
          >
          </USWDSTextInput>
          <USWDSTextInput
            label="Transmission Id"
            @enterPressed="decideBetweenDecapOrSiteInformation"
            v-model.trim="transmissionId"
            aria-label="Transmission Id"
          >
          </USWDSTextInput>
          <USWDSComboBox
            label="State"
            @enterPressed="decideBetweenDecapOrSiteInformation"
            v-model="stateName"
            aria-label="State"
          >
          </USWDSComboBox>
        </div>
        <div class="tablet:grid-col-4">
          <USWDSRadioForm v-model="lastDays" :radioOptions="radioOptions" />
        </div>
      </div>
    </div>
    <button
      class="usa-button"
      type="submit"
      @click="decideBetweenDecapOrSiteInformation"
    >
      Search
    </button>
    <button class="usa-button" type="submit" @click="detailedInformation = ''">
      Back to Table View
    </button>

    <USWDSAlert
      v-if="loading"
      title="Loading"
      text="Please Wait..."
    ></USWDSAlert>
    <USWDSAlert
      v-if="combinedDataIsEmpty && !loading"
      title="No Data"
      text="Please refine your query above"
    ></USWDSAlert>

    <USWDSTable
      :fields="tableColumns"
      :items="tableDataToShow"
      :downloadButton="true"
      :hover="true"
      @rowClicked="showDetails"
      @downloadClicked="downloadConfiguration"
      v-if="!combinedDataIsEmpty"
    >
    </USWDSTable>

    <DetailedPlatformView
      :rowSelected="detailedInformation"
      v-if="detailedInformation"
      @loading="
        (isLoading) => {
          this.loading = isLoading;
        }
      "
    ></DetailedPlatformView>

    <FooterUSGS></FooterUSGS>
  </div>
</template>

<script>
import HeaderUSGS from "@/components/HeaderUSGS.vue";
import USWDSBanner from "@/components/USWDSBanner.vue";
import FooterUSGS from "@/components/FooterUSGS.vue";
import USWDSTextInput from "@/components/USWDSTextInput.vue";
import USWDSComboBox from "@/components/USWDSComboBox.vue";
import USWDSTable from "@/components/USWDSTable.vue";
import USWDSAlert from "@/components/USWDSAlert.vue";
import USWDSRadioForm from "@/components/USWDSRadioForm.vue";
import DetailedPlatformView from "@/components/DetailedPlatformView.vue";
import axios from "axios";
import AuthService from "@/services/AuthService";
import _ from "lodash";

export default {
  name: "App",
  components: {
    HeaderUSGS,
    USWDSBanner,
    FooterUSGS,
    USWDSTextInput,
    USWDSComboBox,
    USWDSTable,
    USWDSAlert,
    USWDSRadioForm,
    DetailedPlatformView,
  },
  data() {
    return {
      tableColumns: [
        { key: "siteNumber", label: "Site Number" },
        { key: "siteName", label: "Site Name" },
        { key: "stateName", label: "State" },
        { key: "transmissionId", label: "Transmission ID" },
        { key: "channel", label: "Channel" },
        { key: "active", label: "Active?" },
        { key: "lastModified", label: "Last Modified" },
      ],
      sensorColumns: [
        { key: "SiteNumber", label: "Sensor Name" },
        { key: "SiteName", label: "AQTS NAme " },
        { key: "StateName", label: "Param Code" },
      ],
      radioOptions: [
        { value: "last-day", display: "Last Day", return: 1 },
        { value: "last-seven", display: "Last 7 Days", return: 7 },
        { value: "last-thirty", display: "Last 30 Days", return: 30 },
      ],
      inputNames: [
        "siteNumber",
        "siteName",
        "transmissionId",
        "stateName",
        "lastDays",
      ],
      combinedData: [],
      siteNumber: "",
      siteName: "",
      stateName: "",
      transmissionId: "",
      lastDays: "0",
      detailedInformation: "",
      loading: false,
      bannerMessage: "",
      testValue: "",
    };
  },
  mounted() {
    if (process.env.VUE_APP_ENVIRONMENT === "dev") {
      AuthService.isUserLoggedIn().then((userLoggedIn) => {
        if (!userLoggedIn) {
          if (AuthService.isLoginRedirect()) {
            AuthService.loginCallback().then(() => {
              history.replaceState({}, document.title, "/");
              this.loggedIn = true;
            });
          } else {
            AuthService.login();
          }
        } else {
          if (AuthService.isLoginRedirect()) {
            AuthService.silentRefreshCallback();
          }
          this.loggedIn = true;
        }
      });

      axios.interceptors.request.use(
        (config) => {
          config.headers.Authorization = `Bearer ${AuthService.getAccessToken()}`;
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    }
  },
  created() {
    document.title = "Configuration Depot";
    axios
      .get(process.env.BASE_URL + "config.json")
      .then((response) => {
        this.bannerMessage = response.data.BANNER_MESSAGE;
      })
      .catch(() => {
        this.bannerMessage =
          "Unable to reach file location on load, there may be errors. Please document this and reach out to your USGS partner.";
      });
    axios.defaults.baseURL = process.env.VUE_APP_API_GATEWAY;
  },
  computed: {
    tableDataToShow() {
      if (!_.isEmpty(this.detailedInformation)) {
        return [this.detailedInformation];
      } else {
        return this.combinedData;
      }
    },

    combinedDataIsEmpty() {
      return _.isEmpty(this.combinedData);
    },
  },
  methods: {
    showDetails(row) {
      this.detailedInformation = row;
    },
    decideBetweenDecapOrSiteInformation() {
      this.detailedInformation = "";
      this.combinedData = [];

      var ogcFilterString = "";
      for (const inputName of this.inputNames) {
        ogcFilterString += this.addParamToOgcString(
          ogcFilterString,
          inputName,
          this[inputName]
        );
      }
      this.searchDecapApiForAll(ogcFilterString);
    },
    addParamToOgcString(ogcFilterString, paramName, param) {
      if (paramName === "siteName" && param != null) {
        param = param.toUpperCase();
      }
      return _.isEmpty(param)
        ? ""
        : (_.isEmpty(ogcFilterString) ? "" : "&") + paramName + "=" + param;
    },
    searchDecapApiForAll(filterString) {
      this.loading = true;

      return axios
        .get("search?" + filterString)
        .then((response) => {
          this.combinedData = response.data;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadConfiguration(rowSelected) {
      this.loading = true;
      return axios
        .get("retrieval?platformId=" + rowSelected.platformId)
        .then((response) => {
          if (response.data.config && response.data.platform) {
            var jsonString = JSON.stringify(response.data, null, 2);
            const blob = new Blob([jsonString], { encoding: "UTF-8" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download =
              response.data.platform.siteNumber +
              "_" +
              response.data.platform.transmissionId +
              ".json";
            link.click();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "~uswds/css/uswds.css";
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}
button {
  margin-left: 10px;
  height: 100%;
  margin-top: 5px;
}
.grid-container {
  margin-left: 0px;
  padding-left: 0px;
}
.tablet\:grid-col-4 {
  margin-right: 1rem;
}
.usa-combo-box__toggle-list,
.usa-combo-box__clear-input {
  margin-top: 0;
}
</style>
